import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CDFTService } from 'app/modules/cdft/services/cdft.service';
import { CDFTTable } from 'app/modules/cdft/model/cdft.model';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import module from 'app/modules/cdft/cdft.module';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-offline-sync-modal',
    standalone: true,
    imports: [
        CommonModule,
        MatProgressBarModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        module,
    ],
    template: `
        <div class="p-6 max-w-2xl">
            <div class="flex items-center gap-4 mb-6">
                <mat-icon
                    class="text-primary"
                    svgIcon="heroicons_solid:cloud-arrow-down"
                ></mat-icon>
                <div>
                    <h2 class="text-xl font-semibold">Offline Data Sync</h2>
                    <p class="text-sm text-gray-500">
                        Cache all CDFT records for offline access
                    </p>
                </div>
            </div>

            <div class="space-y-4 mb-6">
                <p class="text-sm text-gray-700">
                    This will download and cache all CDFT records on your
                    device, including:
                </p>
                <ul
                    class="list-disc list-inside space-y-1 text-sm text-gray-600"
                >
                    <li>CDFT table records</li>
                    <li>CDFT details for each record</li>
                    <li>String equipment information</li>
                    <li>Well production categories</li>
                    <li>Well string status</li>
                </ul>
                <p class="text-sm text-gray-700 mt-4">
                    Once cached, you can view and edit these records even
                    without an internet connection.
                </p>
            </div>

            <!-- Progress Section -->
            <div *ngIf="isSyncing" class="mb-6">
                <div class="flex justify-between items-center mb-2">
                    <span class="text-sm font-medium">
                        Caching: {{ currentRecord }} / {{ totalRecords }}
                    </span>
                    <span class="text-xs text-gray-500">
                        {{ progress | number: '1.0-0' }}%
                    </span>
                </div>
                <mat-progress-bar
                    mode="determinate"
                    [value]="progress"
                    color="primary"
                ></mat-progress-bar>
                <p class="text-xs text-gray-500 mt-2">
                    {{ statusMessage }}
                </p>
            </div>

            <!-- Success Message -->
            <div
                *ngIf="syncComplete && !isSyncing && failedRecords.length === 0"
                class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6"
            >
                <div class="flex items-center gap-2">
                    <mat-icon
                        class="text-green-600"
                        svgIcon="heroicons_solid:check-circle"
                    ></mat-icon>
                    <div>
                        <p class="text-sm font-medium text-green-800">
                            Sync Complete!
                        </p>
                        <p class="text-xs text-green-700">
                            {{ currentRecord }} records have been cached
                            successfully.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Partial Success Message -->
            <div
                *ngIf="syncComplete && !isSyncing && failedRecords.length > 0"
                class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6"
            >
                <div class="flex items-start gap-2">
                    <mat-icon
                        class="text-yellow-600 mt-1"
                        svgIcon="heroicons_solid:exclamation-triangle"
                    ></mat-icon>
                    <div class="flex-1">
                        <p class="text-sm font-medium text-yellow-800">
                            Sync Completed with Errors
                        </p>
                        <p class="text-xs text-yellow-700 mt-1">
                            {{ currentRecord - failedRecords.length }} records
                            cached successfully.
                            {{ failedRecords.length }} failed to cache:
                        </p>
                        <ul
                            class="list-disc list-inside text-xs text-yellow-700 mt-2 max-h-24 overflow-y-auto"
                        >
                            <li *ngFor="let failedRecord of failedRecords">
                                {{ failedRecord.name }}:
                                {{ failedRecord.error }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- Buttons -->
            <div class="flex justify-end gap-3">
                <button
                    *ngIf="!syncComplete"
                    mat-stroked-button
                    (click)="onCancel()"
                    [disabled]="isSyncing"
                >
                    Cancel
                </button>
                <button
                    mat-raised-button
                    color="primary"
                    (click)="syncComplete ? onCancel() : startSync()"
                    [disabled]="isSyncing"
                >
                    <mat-icon
                        *ngIf="isSyncing"
                        class="animate-spin mr-2"
                        svgIcon="heroicons_solid:arrow-path"
                    ></mat-icon>
                    {{
                        isSyncing
                            ? 'Syncing...'
                            : syncComplete
                              ? 'Close'
                              : 'Start Sync'
                    }}
                </button>
            </div>
        </div>
    `,
    styles: [
        `
            :host ::ng-deep {
                .animate-spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            }
        `,
    ],
})
export class OfflineSyncModalComponent implements OnInit, OnDestroy {
    isSyncing = false;
    syncComplete = false;
    progress = 0;
    currentRecord = 0;
    totalRecords = 0;
    statusMessage = '';
    failedRecords: Array<{ id: string; name: string; error: string }> = [];

    private readonly destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<OfflineSyncModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { cdftRecords: CDFTTable[] },
        private readonly cdftService: CDFTService,
    ) {}

    ngOnInit(): void {
        this.totalRecords = this.data.cdftRecords.length;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    startSync(): void {
        this.isSyncing = true;
        this.syncComplete = false;
        this.failedRecords = [];
        this.currentRecord = 0;
        this.progress = 0;

        // Cache global options first
        this.statusMessage = 'Caching global options...';
        forkJoin([
            this.cdftService
                .getValveOptionType()
                .pipe(catchError(() => of(null))),
            this.cdftService
                .getSSSVOptionType()
                .pipe(catchError(() => of(null))),
        ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.syncAllRecords();
            });
    }

    private syncAllRecords(): void {
        if (this.currentRecord >= this.totalRecords) {
            this.isSyncing = false;
            this.syncComplete = true;
            return;
        }

        const record = this.data.cdftRecords[this.currentRecord];
        this.statusMessage = `Caching CDFT ${record.id}...`;

        // Fetch CDFT detail and related data
        this.cdftService
            .getCdft(record.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    // Get wellStringId from detailed response fallback to record
                    const wellStringId =
                        res.data?.cdftHeader?.wellStringId ||
                        record.wellStringId;

                    // Fetch string equipment and production category if needed
                    if (wellStringId) {
                        forkJoin([
                            this.cdftService
                                .getStringEquipment(wellStringId)
                                .pipe(catchError(() => of(null))),
                            this.cdftService
                                .getWellProductionCategory(wellStringId)
                                .pipe(catchError(() => of(null))),
                            this.cdftService
                                .getWellString(wellStringId)
                                .pipe(catchError(() => of(null))),
                        ])
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                                next: () => {
                                    this.onRecordSynced();
                                },
                                error: (err) => {
                                    // Should not reach here due to catchError above, but for safety
                                    this.failedRecords.push({
                                        id: record.id,
                                        name:
                                            record.wellLongName ||
                                            record.id ||
                                            'Unknown Record',
                                        error: 'Failed to cache related data',
                                    });
                                    this.onRecordSynced();
                                },
                            });
                    } else {
                        this.onRecordSynced();
                    }
                },
                error: (err) => {
                    // Log error but continue with next record
                    this.failedRecords.push({
                        id: record.id,
                        name:
                            record.wellLongName ||
                            record.id ||
                            'Unknown Record',
                        error: 'Failed to cache CDFT details',
                    });
                    this.onRecordSynced();
                },
            });
    }

    private onRecordSynced(): void {
        this.currentRecord++;
        this.progress = (this.currentRecord / this.totalRecords) * 100;
        this.syncAllRecords();
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}
